import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import { SidebarButton } from '@gitbook/spine-react';

import {
    APIErrorBoundary,
    NotificationsBell,
    OrganizationSearchButton,
    SearchButton,
} from '../Components';
import { apiQuery } from '../state';

export function MobileHeader(props: {
    onMenuOpenChange: (isOpened: boolean) => void;
    isMenuOpen: boolean;
    screen: NavigationScreenState;
}) {
    const { onMenuOpenChange, isMenuOpen, screen } = props;
    const organizationId =
        'organization' in screen && screen.organization ? screen.organization : null;

    return (
        <div className="flex h-toolbar-header items-center justify-between border-b bg-side px-3 md:hidden">
            <div className="flex items-center gap-1">
                <SidebarButton
                    data-testid="sidebar.toggleMobileButton"
                    icon={Icon.Menu}
                    iconOnly
                    label={isMenuOpen ? 'Hide sidebar' : 'Show sidebar'}
                    onPress={() => onMenuOpenChange(true)}
                />
                <Title screen={screen} organizationId={organizationId} />
            </div>
            <div className="flex gap-1">
                {organizationId ? (
                    <React.Suspense fallback={<SearchButton aria-busy ai={false} />}>
                        <WithOrgSearchButton organizationId={organizationId} />
                    </React.Suspense>
                ) : (
                    <SearchButton ai={false} />
                )}
                <NotificationsBell />
            </div>
        </div>
    );
}

function WithOrgSearchButton(props: { organizationId: string }) {
    const { organizationId } = props;
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    return (
        <OrganizationSearchButton
            data-testid="sidebar.findMobileButton"
            organization={organization}
        />
    );
}

function Title(props: { screen: NavigationScreenState; organizationId: string | null }) {
    const { screen, organizationId } = props;
    return (
        <h2 className="text-ui-standout">
            {(() => {
                switch (screen.type) {
                    case 'account':
                    case 'org_settings':
                        return 'Settings';
                    default:
                        return organizationId ? (
                            <APIErrorBoundary fallback={{ 404: <>Not found</> }}>
                                <React.Suspense fallback={<span aria-busy>Loading...</span>}>
                                    <OrganizationTitle organizationId={organizationId} />
                                </React.Suspense>
                            </APIErrorBoundary>
                        ) : null;
                }
            })()}
        </h2>
    );
}

function OrganizationTitle(props: { organizationId: string }) {
    const { organizationId } = props;
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    return organization.title;
}
